import { useState } from "react";
import type { ReturnTo } from "../domain/contracts";
import { useAppNavigation } from "../hooks/useAppNavigation";
import { AppShell } from "../components/layout/AppShell";
import { HeroSection } from "../components/layout/HeroSection";
import { MapSection } from "../features/map/MapSection";
import { VillageSection } from "../features/village/VillageSection";
import { VillagesPage } from "../features/village/VillagesPage";
import { IntervieweeSection } from "../features/interviewee/IntervieweeSection";
import { IntervieweesPage } from "../features/interviewee/IntervieweesPage";
import { useAppViewModel } from "./useAppViewModel";

export function App() {
  const viewModel = useAppViewModel();
  // viewMode is derived from the URL (via routeSelection), never from local state.
  // This ensures page refresh restores the correct view from URL params.
  const viewMode = viewModel.route.viewRef ?? "map";
  const [returnTo, setReturnTo] = useState<ReturnTo>("map");

  const nav = useAppNavigation({
    returnTo,
    setReturnTo,
    setViewRef: viewModel.route.setViewRef,
    selectVillage: viewModel.handlers.selectVillage,
    selectInterviewee: viewModel.handlers.selectInterviewee,
    clearInterviewee: viewModel.handlers.clearInterviewee,
  });


  const showMapView = viewMode === "map";
  const showVillageView = viewMode === "village";
  const showIntervieweeView = viewMode === "interviewee";
  const showVillagesList = viewMode === "villagesList";
  const showIntervieweesList = viewMode === "intervieweesList";
  const activeNav = showVillagesList || showVillageView
    ? "villages"
    : showIntervieweesList || showIntervieweeView
      ? "interviewees"
      : "home";

  return (
    <AppShell
      banner={showMapView ? <HeroSection /> : null}
      onHeaderNav={nav.handleHeaderNav}
      activeNav={activeNav}
    >
      {showMapView && (
        <>
          <MapSection
            villages={viewModel.mapVillages}
            previewVillages={viewModel.previewVillages}
            selectedVillage={viewModel.selectedVillage}
            onSelectVillage={viewModel.handlers.selectVillage}
            onViewFullProfile={nav.handleViewFullProfile}
          />
        </>
      )}

      {showVillagesList && (
        <VillagesPage
          villages={viewModel.mapVillages}
          onSelectVillage={nav.handleSelectVillageFromList}
        />
      )}

      {showIntervieweesList && (
        <IntervieweesPage
          onSelectInterviewee={nav.handleSelectIntervieweeFromList}
        />
      )}

      {showVillageView && viewModel.selectedVillage && (
        <VillageSection
          village={viewModel.selectedVillage}
          interviewees={viewModel.interviewees}
          evidenceItems={viewModel.evidenceItems}
          isIntervieweesLoading={viewModel.loading.village.isIntervieweesLoading}
          isEvidenceLoading={viewModel.loading.village.isEvidenceLoading}
          villageFacts={viewModel.villageFacts}
          intervieweeDirectorySummary={viewModel.intervieweeDirectorySummary}
          evidenceOverviewLines={viewModel.evidenceOverviewLines}
          incidentEntries={viewModel.incidentEntries}
          incidentOverview={viewModel.incidentOverview}
          evidenceEntries={viewModel.evidenceEntries}
          onSelectInterviewee={nav.handleSelectIntervieweeFromVillage}
          onLoadMoreInterviewees={viewModel.handlers.loadMoreInterviewees}
          canLoadMoreInterviewees={viewModel.pagination.canLoadMoreInterviewees}
          isLoadingMoreInterviewees={viewModel.pagination.isLoadingMoreInterviewees}
          onToggleIncidentOverview={viewModel.handlers.toggleIncidentOverview}
          returnTo={returnTo}
          onBack={nav.handleBackToList}
        />
      )}

      {showIntervieweeView && (
        <IntervieweeSection
          village={viewModel.selectedVillage}
          selectedInterviewee={viewModel.selectedInterviewee}
          intervieweeDetail={viewModel.intervieweeDetail}
          mediaPreviews={viewModel.intervieweeMediaPreviews}
          isLoadingDetail={viewModel.loading.interviewee.isDetailLoading}
          returnTo={returnTo}
          onBack={nav.handleBackToList}
        />
      )}
    </AppShell>
  );
}